import { useState, useEffect } from 'react';
import { formatDatePT } from '../utils/dateFormat';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Calendar, Users, Building2, Clock, Plane, TrendingUp, Archive, RotateCcw, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import DetailedReport from './DetailedReport';

const ReportsComplete = ({ user }) => {
  const { t } = useTranslation();
  // Estados gerais
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  
  // Relatórios
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState(null);
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    user_id: '',
    client: '',
    address: '',
  });
  
  // Enderecos disponíveis
  const [addresses, setAddresses] = useState([]);
  
  // Arquivar Horas
  const [archiveUser, setArchiveUser] = useState('');
  const [archiveStart, setArchiveStart] = useState('');
  const [archiveEnd, setArchiveEnd] = useState('');
  const [archivePreview, setArchivePreview] = useState([]);
  const [archivePreviewLoading, setArchivePreviewLoading] = useState(false);
  const [archivedEntries, setArchivedEntries] = useState([]);
  const [archivedFilters, setArchivedFilters] = useState({
    user_id: 'all',
    start_date: '',
    end_date: '',
    client: 'all'
  });
  
  // Relatório Detalhado
  const [showDetailedReport, setShowDetailedReport] = useState(false);
  const [detailedReportData, setDetailedReportData] = useState(null);
  
  // Horas em Aberto
  const [openHours, setOpenHours] = useState([]);
  const [openHoursFilters, setOpenHoursFilters] = useState({
    user_id: 'all',
    client: 'all',
    start_date: '',
    end_date: ''
  });
  const [totalOpenHours, setTotalOpenHours] = useState(0);

  useEffect(() => {
    if (user.is_admin) {
      fetchUsers();
      fetchClients();
      fetchReports();
      fetchAddresses();
    }
    fetchOpenHours();
  }, []);

  // Auto-apply filters for Open Hours
  useEffect(() => {
    fetchOpenHours();
  }, [openHoursFilters.user_id, openHoursFilters.client, openHoursFilters.start_date, openHoursFilters.end_date]);

  // Auto-apply filters for Reports
  useEffect(() => {
    if (user.is_admin) {
      fetchReports();
    }
  }, [filters.start_date, filters.end_date, filters.user_id, filters.client, filters.address]);

  const fetchUsers = async () => {
    try {
      const response = await axiosInstance.get('/users');
      // Incluir TODOS os usuários, incluindo admins
      console.log('👥 Usuários carregados:', response.data.length, response.data.map(u => u.name));
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axiosInstance.get('/clients/all');
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients');
    }
  };

  const fetchAddresses = async (clientName) => {
    try {
      const params = clientName ? { client_name: clientName } : {};
      const response = await axiosInstance.get('/addresses', { params });
      setAddresses(response.data || []);
    } catch (error) {
      console.error('Error fetching addresses');
      setAddresses([]);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.client) params.client = filters.client;
      if (filters.address) params.address = filters.address;

      const response = await axiosInstance.get('/reports', { params });
      setReports(response.data);
    } catch (error) {
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  // Buscar Horas em Aberto
  const fetchOpenHours = async () => {
    try {
      const params = { limit: 2000, archived: 'false' };
      if (openHoursFilters.user_id && openHoursFilters.user_id !== 'all') {
        params.user_id = openHoursFilters.user_id;
      }
      if (openHoursFilters.client && openHoursFilters.client !== 'all') {
        params.client = openHoursFilters.client;
      }
      if (openHoursFilters.start_date) {
        params.start_date = openHoursFilters.start_date;
      }
      if (openHoursFilters.end_date) {
        params.end_date = openHoursFilters.end_date;
      }
      
      const response = await axiosInstance.get('/time-entries', { params });
      const allEntries = response.data.entries || response.data;
      
      // Only keep pending/approved (non-archived already filtered server-side)
      const openEntries = allEntries.filter(entry => 
        entry.status === 'pending' || entry.status === 'approved'
      );
      
      setOpenHours(openEntries);
      
      const total = openEntries.reduce((sum, entry) => 
        sum + (entry.hours_worked || 0), 0
      );
      
      setTotalOpenHours(total);
    } catch (error) {
      console.error('Erro ao buscar horas:', error);
      toast.error('Erro ao carregar horas em aberto');
    }
  };

  // Arquivar Functions
  const fetchArchivedEntries = async () => {
    try {
      const params = { limit: 2000, archived: 'true' };
      if (archivedFilters.user_id && archivedFilters.user_id !== 'all') {
        params.user_id = archivedFilters.user_id;
      }
      if (archivedFilters.client && archivedFilters.client !== 'all') {
        params.client = archivedFilters.client;
      }
      if (archivedFilters.start_date) params.start_date = archivedFilters.start_date;
      if (archivedFilters.end_date) params.end_date = archivedFilters.end_date;
      const response = await axiosInstance.get('/time-entries', { params });
      const allEntries = response.data.entries || response.data || [];
      setArchivedEntries(allEntries);
    } catch (error) {
      toast.error('Erro ao carregar horas arquivadas');
    }
  };

  const fetchArchivePreview = async () => {
    if (!archiveUser || !archiveStart || !archiveEnd) {
      setArchivePreview([]);
      return;
    }
    setArchivePreviewLoading(true);
    try {
      const params = {
        limit: 2000,
        archived: 'false',
        user_id: archiveUser,
        start_date: archiveStart,
        end_date: archiveEnd,
        status: 'approved',
      };
      const response = await axiosInstance.get('/time-entries', { params });
      const entries = response.data.entries || response.data || [];
      setArchivePreview(entries);
    } catch (error) {
      setArchivePreview([]);
    } finally {
      setArchivePreviewLoading(false);
    }
  };

  useEffect(() => {
    fetchArchivedEntries();
  }, [archivedFilters.user_id, archivedFilters.client, archivedFilters.start_date, archivedFilters.end_date]);

  useEffect(() => {
    fetchArchivePreview();
  }, [archiveUser, archiveStart, archiveEnd]);

  const handleArchive = async () => {
    if (!archiveUser || !archiveStart || !archiveEnd) {
      toast.error('Preencha funcionário e período');
      return;
    }
    if (archivePreview.length === 0) {
      toast.error('Nenhuma hora aprovada encontrada para este período');
      return;
    }

    try {
      const res = await axiosInstance.put('/time-entries/archive', {
        user_id: archiveUser,
        start_date: archiveStart,
        end_date: archiveEnd
      });
      const count = res.data?.archived_count ?? archivePreview.length;
      toast.success(`${count} registro(s) arquivado(s) com sucesso!`);
      
      // Atualizar todas as listas
      setArchivePreview([]);
      fetchArchivedEntries();
      fetchOpenHours();
      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao arquivar horas');
    }
  };

  const handleUnarchive = async () => {
    if (!archiveUser || !archiveStart || !archiveEnd) {
      toast.error('Preencha funcionário e período');
      return;
    }

    try {
      const res = await axiosInstance.put('/time-entries/unarchive', {
        user_id: archiveUser,
        start_date: archiveStart,
        end_date: archiveEnd
      });
      const count = res.data?.unarchived_count ?? 0;
      toast.success(`${count} registro(s) desarquivado(s) com sucesso!`);
      
      fetchArchivedEntries();
      fetchOpenHours();
      fetchReports();
      fetchArchivePreview();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desarquivar horas');
    }
  };

  const handleShowDetailedReport = async () => {
    if (!filters.start_date || !filters.end_date) {
      toast.error('Selecione o período (datas inicial e final)');
      return;
    }
    
    if (!filters.user_id) {
      toast.error('Selecione um funcionário específico para ver o relatório detalhado');
      return;
    }

    try {
      const params = {
        limit: 2000,
        user_id: filters.user_id,
        start_date: filters.start_date,
        end_date: filters.end_date
      };
      const response = await axiosInstance.get('/time-entries', { params });
      const filteredEntries = response.data.entries || response.data || [];
      
      const userData = users.find(u => u.id === filters.user_id);
      
      if (!userData) {
        toast.error('Funcionário não encontrado');
        return;
      }
      
      setDetailedReportData({
        user: userData,
        entries: filteredEntries,
        weekStart: filters.start_date,
        weekEnd: filters.end_date
      });
      setShowDetailedReport(true);
    } catch (error) {
      toast.error('Erro ao carregar relatório detalhado');
      console.error(error);
    }
  };

  // Resolves the eindklant label from a time-entry by matching entry.project (= address)
  // to clients[].addresses[].address. Returns {label, bedrijf} so the UI can show the
  // real klant prominently while keeping the bedrijf as a subtitle.
  const resolveEindklant = (entry) => {
    const out = { label: entry.client || '', bedrijf: '' };
    const proj = (entry.project || '').trim().toLowerCase();
    if (!proj) return out;
    for (const c of clients) {
      const m = (c.addresses || []).find(a => (a.address || '').trim().toLowerCase() === proj);
      if (m) {
        if (m.label) out.label = m.label;
        out.bedrijf = c.name;
        break;
      }
    }
    return out;
  };

  // Funcionários veem apenas uma versão simplificada com suas horas
  if (!user.is_admin) {
    return (
      <div className="space-y-6">
        <h1 className="text-4xl font-bold text-gray-800">Meus Relatórios</h1>
        
        {/* Card de Resumo */}
        <Card className="bg-gradient-to-r from-[#0d3d3d] to-[#1a5555] border-transparent shadow-lg">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Minhas Horas em Aberto</p>
                <h2 className="text-4xl font-bold text-white">{totalOpenHours.toFixed(1)}h</h2>
                <p className="text-white/50 text-xs mt-1">{openHours.length} registros</p>
              </div>
              <Clock className="w-16 h-16 text-white/20" />
            </div>
          </CardContent>
        </Card>

        {/* Lista de Horas */}
        <Card className="bg-white shadow-sm border-gray-200">
          <CardHeader>
            <CardTitle className="text-gray-800">Meus Registros de Horas</CardTitle>
          </CardHeader>
          <CardContent>
            {openHours.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <p className="text-gray-400">Nenhuma hora encontrada</p>
              </div>
            ) : (
              <div className="space-y-3">
                {openHours.map(entry => {
                  const eind = resolveEindklant(entry);
                  return (
                  <div key={entry.id} className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-[#3da5a5] text-sm font-semibold">{entry.date}</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                          <div>
                            <span className="text-gray-400">Klant: </span>
                            <span className="text-gray-800 font-semibold">{eind.label || entry.client}</span>
                            {eind.bedrijf && eind.bedrijf.toLowerCase() !== (eind.label || entry.client || '').toLowerCase() && (
                              <span className="text-gray-500 text-[11px] ml-1">({eind.bedrijf})</span>
                            )}
                          </div>
                          <div>
                            <span className="text-gray-400">Horário: </span>
                            <span className="text-gray-800">{entry.start_time} - {entry.end_time}</span>
                          </div>
                        </div>
                        {entry.description && (
                          <p className="text-gray-400 text-sm mt-2">{entry.description}</p>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="text-gray-800 font-bold text-lg">{entry.hours_worked}h</span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          entry.status === 'approved' 
                            ? 'bg-green-600/20 text-green-400' 
                            : 'bg-yellow-600/20 text-yellow-400'
                        }`}>
                          {entry.status === 'approved' ? 'Aprovado' : 'Pendente'}
                        </span>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-4xl font-bold text-gray-800">Relatórios & Gestão</h1>

      <Tabs defaultValue="open-hours" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-gray-50">
          <TabsTrigger value="open-hours">⏰ Horas em Aberto</TabsTrigger>
          <TabsTrigger value="reports">📊 Relatórios</TabsTrigger>
          <TabsTrigger value="archive">📦 Arquivar Horas</TabsTrigger>
        </TabsList>

        {/* TAB: Horas em Aberto */}
        <TabsContent value="open-hours" className="space-y-6">
          {/* Card de Resumo */}
          <Card className="bg-gradient-to-r from-[#0d3d3d] to-[#1a5555] border-transparent shadow-lg">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/70 text-sm">Total de Horas em Aberto</p>
                  <h2 className="text-4xl font-bold text-white">{totalOpenHours.toFixed(1)}h</h2>
                  <p className="text-white/50 text-xs mt-1">{openHours.length} registros</p>
                </div>
                <Clock className="w-16 h-16 text-white/20" />
              </div>
            </CardContent>
          </Card>

          {/* Filtros */}
          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader>
              <CardTitle className="text-gray-800">Filtros de Pesquisa</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-gray-800">Funcionário</Label>
                  <Select
                    value={openHoursFilters.user_id || "all"}
                    onValueChange={(value) => {
                      console.log('👤 Funcionário selecionado:', value);
                      setOpenHoursFilters({ ...openHoursFilters, user_id: value });
                    }}
                  >
                    <SelectTrigger className="bg-gray-50 border-white/30 text-gray-800">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-800">Cliente</Label>
                  <Select
                    value={openHoursFilters.client || "all"}
                    onValueChange={(value) => {
                      console.log('🏢 Cliente selecionado:', value);
                      setOpenHoursFilters({ ...openHoursFilters, client: value });
                    }}
                  >
                    <SelectTrigger className="bg-gray-50 border-white/30 text-gray-800">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-800">Data Inicial</Label>
                  <Input
                    type="date"
                    value={openHoursFilters.start_date}
                    onChange={(e) => setOpenHoursFilters({ ...openHoursFilters, start_date: e.target.value })}
                    className="bg-gray-50 border-white/30 text-gray-800"
                  />
                </div>
                <div>
                  <Label className="text-gray-800">Data Final</Label>
                  <Input
                    type="date"
                    value={openHoursFilters.end_date}
                    onChange={(e) => setOpenHoursFilters({ ...openHoursFilters, end_date: e.target.value })}
                    className="bg-gray-50 border-white/30 text-gray-800"
                  />
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button 
                  onClick={() => {
                    setOpenHoursFilters({ user_id: 'all', client: 'all', start_date: '', end_date: '' });
                  }}
                  variant="outline"
                  className="border-gray-300 text-gray-800"
                >
                  Limpar Filtros
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Lista de Horas em Aberto */}
          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader>
              <CardTitle className="text-gray-800">Registros de Horas em Aberto</CardTitle>
            </CardHeader>
            <CardContent>
              {openHours.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-16 h-16 text-white/20 mx-auto mb-4" />
                  <p className="text-gray-400">Nenhuma hora em aberto encontrada</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {openHours.map(entry => {
                    const eind = resolveEindklant(entry);
                    return (
                    <div key={entry.id} className="bg-gray-50 p-4 rounded-lg border border-gray-100">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="text-gray-800 font-semibold">{entry.user_name}</span>
                            <span className="text-gray-400 text-sm">•</span>
                            <span className="text-[#3da5a5] text-sm">{entry.date}</span>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                            <div>
                              <span className="text-gray-400">Klant: </span>
                              <span className="text-gray-800 font-semibold">{eind.label || entry.client}</span>
                              {eind.bedrijf && eind.bedrijf.toLowerCase() !== (eind.label || entry.client || '').toLowerCase() && (
                                <span className="text-gray-500 text-[11px] ml-1">({eind.bedrijf})</span>
                              )}
                            </div>
                            <div>
                              <span className="text-gray-400">Horário: </span>
                              <span className="text-gray-800">{entry.start_time} - {entry.end_time}</span>
                            </div>
                            <div>
                              <span className="text-gray-400">Horas: </span>
                              <span className="text-gray-800 font-semibold">{entry.hours_worked}h</span>
                            </div>
                          </div>
                          {entry.description && (
                            <p className="text-gray-400 text-sm mt-2">{entry.description}</p>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-1">
                          <span className={`text-xs px-2 py-1 rounded ${
                            entry.status === 'approved' 
                              ? 'bg-green-600/20 text-green-400' 
                              : 'bg-yellow-600/20 text-yellow-400'
                          }`}>
                            {entry.status === 'approved' ? 'Aprovado' : 'Pendente'}
                          </span>
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Relatórios */}
        <TabsContent value="reports" className="space-y-6">
          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader>
              <CardTitle className="text-gray-800">Filtros</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-800">Data Inicial</Label>
                  <Input
                    type="date"
                    value={filters.start_date}
                    onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                    className="bg-gray-50 border-gray-200 text-gray-800"
                  />
                </div>
                <div>
                  <Label className="text-gray-800">Data Final</Label>
                  <Input
                    type="date"
                    value={filters.end_date}
                    onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                    className="bg-gray-50 border-gray-200 text-gray-800"
                  />
                </div>
                <div>
                  <Label className="text-gray-800">Funcionário</Label>
                  <Select value={filters.user_id || "all"} onValueChange={(value) => setFilters({ ...filters, user_id: value === "all" ? "" : value })}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-800">Cliente</Label>
                  <Select value={filters.client || "all"} onValueChange={(value) => {
                    const clientVal = value === "all" ? "" : value;
                    setFilters({ ...filters, client: clientVal, address: '' });
                    if (clientVal) fetchAddresses(clientVal);
                    else fetchAddresses();
                  }}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-800">Endereço</Label>
                  <Select 
                    value={filters.address || "all"} 
                    onValueChange={(value) => setFilters({ ...filters, address: value === "all" ? "" : value })}
                  >
                    <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800" data-testid="report-address-filter">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {addresses.map((addr, i) => (
                        <SelectItem key={i} value={addr}>{addr}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end gap-2">
                  <Button 
                    onClick={() => setFilters({ start_date: '', end_date: '', user_id: '', client: '', address: '' })}
                    variant="outline"
                    className="border-gray-300 text-gray-800"
                  >
                    Limpar
                  </Button>
                  <Button 
                    onClick={handleShowDetailedReport} 
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={!filters.user_id}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Detalhado
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {reports && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0">
                  <CardContent className="py-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Total de Horas</p>
                        <p className="text-3xl font-bold text-white">{reports.total_hours?.toFixed(1) || 0}h</p>
                      </div>
                      <Clock className="w-12 h-12 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-600 to-green-700 border-0">
                  <CardContent className="py-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Horas Viagem</p>
                        <p className="text-3xl font-bold text-white">{reports.total_travel_hours?.toFixed(1) || 0}h</p>
                      </div>
                      <Plane className="w-12 h-12 text-green-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-600 to-purple-700 border-0">
                  <CardContent className="py-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-100 text-sm">Funcionários</p>
                        <p className="text-3xl font-bold text-white">{reports.total_users || 0}</p>
                      </div>
                      <Users className="w-12 h-12 text-purple-200" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-orange-600 to-orange-700 border-0">
                  <CardContent className="py-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm">Projetos</p>
                        <p className="text-3xl font-bold text-white">{reports.total_projects || 0}</p>
                      </div>
                      <Building2 className="w-12 h-12 text-orange-200" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Per Employee Breakdown */}
              {reports.by_user && Object.keys(reports.by_user).length > 0 && (
                <Card className="bg-white shadow-sm border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-gray-800 flex items-center gap-2">
                      <Users className="w-5 h-5 text-[#3da5a5]" />
                      Horas por Funcionário
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 text-gray-600 font-semibold">Funcionário</th>
                            <th className="text-right py-3 px-4 text-gray-600 font-semibold">Horas Trabalho</th>
                            <th className="text-right py-3 px-4 text-gray-600 font-semibold">Horas Viagem</th>
                            <th className="text-right py-3 px-4 text-gray-600 font-semibold">Total</th>
                            <th className="text-right py-3 px-4 text-gray-600 font-semibold">Registros</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(reports.by_user).map(([uid, data]) => (
                            <tr key={uid} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-4 font-medium text-gray-800">{data.user_name}</td>
                              <td className="py-3 px-4 text-right text-gray-700">{data.total_hours?.toFixed(1)}h</td>
                              <td className="py-3 px-4 text-right text-gray-500">{data.total_travel_hours?.toFixed(1)}h</td>
                              <td className="py-3 px-4 text-right font-semibold text-[#3da5a5]">{(data.total_hours + data.total_travel_hours).toFixed(1)}h</td>
                              <td className="py-3 px-4 text-right text-gray-500">{data.entry_count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Detailed Entries Table */}
              {reports.entries && reports.entries.length > 0 && (
                <Card className="bg-white shadow-sm border-gray-200">
                  <CardHeader>
                    <CardTitle className="text-gray-800 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-[#3da5a5]" />
                      Registros Detalhados ({reports.entries.length})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-3 text-gray-600 font-semibold">Data</th>
                            <th className="text-left py-3 px-3 text-gray-600 font-semibold">Funcionário</th>
                            <th className="text-left py-3 px-3 text-gray-600 font-semibold">Cliente</th>
                            <th className="text-left py-3 px-3 text-gray-600 font-semibold">Endereço</th>
                            <th className="text-center py-3 px-3 text-gray-600 font-semibold">Horário</th>
                            <th className="text-right py-3 px-3 text-gray-600 font-semibold">Horas</th>
                            <th className="text-right py-3 px-3 text-gray-600 font-semibold">Viagem</th>
                            <th className="text-center py-3 px-3 text-gray-600 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reports.entries.map((entry) => {
                            const eind = resolveEindklant(entry);
                            return (
                            <tr key={entry.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-3 px-3 text-gray-800 font-medium whitespace-nowrap">
                                {formatDatePT(entry.date)}
                              </td>
                              <td className="py-3 px-3 text-gray-700">{entry.user_name}</td>
                              <td className="py-3 px-3 text-gray-700">
                                <span className="font-semibold">{eind.label || entry.client}</span>
                                {eind.bedrijf && eind.bedrijf.toLowerCase() !== (eind.label || entry.client || '').toLowerCase() && (
                                  <span className="block text-[10px] text-gray-400">{eind.bedrijf}</span>
                                )}
                              </td>
                              <td className="py-3 px-3 text-gray-500 text-xs">{entry.project || entry.address || '-'}</td>
                              <td className="py-3 px-3 text-center text-gray-600 whitespace-nowrap">
                                {entry.start_time} - {entry.end_time}
                              </td>
                              <td className="py-3 px-3 text-right font-semibold text-gray-800">{entry.hours_worked}h</td>
                              <td className="py-3 px-3 text-right text-gray-500">{entry.travel_hours || 0}h</td>
                              <td className="py-3 px-3 text-center">
                                <span className={`text-xs px-2 py-1 rounded-full ${
                                  entry.status === 'approved' 
                                    ? 'bg-green-100 text-green-700' 
                                    : entry.status === 'rejected'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {entry.status === 'approved' ? 'Aprovado' : entry.status === 'rejected' ? 'Rejeitado' : 'Pendente'}
                                </span>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>

        {/* TAB: Arquivar Horas */}
        <TabsContent value="archive" className="space-y-6">
          {/* Open Hours Per Employee Summary */}
          {openHours.length > 0 && (
            <Card className="bg-white shadow-sm border-gray-200">
              <CardHeader>
                <CardTitle className="text-gray-800 flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#3da5a5]" />
                  Horas em Aberto por Funcionário
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-gray-600 font-semibold">Funcionário</th>
                        <th className="text-right py-3 px-4 text-gray-600 font-semibold">Horas Trabalho</th>
                        <th className="text-right py-3 px-4 text-gray-600 font-semibold">Horas Viagem</th>
                        <th className="text-right py-3 px-4 text-gray-600 font-semibold">Total</th>
                        <th className="text-right py-3 px-4 text-gray-600 font-semibold">Registros</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const byEmployee = {};
                        openHours.forEach(entry => {
                          const name = entry.user_name || 'Desconhecido';
                          if (!byEmployee[name]) byEmployee[name] = { hours: 0, travel: 0, count: 0 };
                          byEmployee[name].hours += entry.hours_worked || 0;
                          byEmployee[name].travel += entry.travel_hours || 0;
                          byEmployee[name].count += 1;
                        });
                        return Object.entries(byEmployee).sort((a, b) => (b[1].hours + b[1].travel) - (a[1].hours + a[1].travel)).map(([name, data]) => (
                          <tr key={name} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-4 font-medium text-gray-800">{name}</td>
                            <td className="py-3 px-4 text-right text-gray-700">{data.hours.toFixed(1)}h</td>
                            <td className="py-3 px-4 text-right text-gray-500">{data.travel.toFixed(1)}h</td>
                            <td className="py-3 px-4 text-right font-semibold text-[#3da5a5]">{(data.hours + data.travel).toFixed(1)}h</td>
                            <td className="py-3 px-4 text-right text-gray-500">{data.count}</td>
                          </tr>
                        ));
                      })()}
                      <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
                        <td className="py-3 px-4 text-gray-800">Total</td>
                        <td className="py-3 px-4 text-right text-gray-800">{openHours.reduce((s, e) => s + (e.hours_worked || 0), 0).toFixed(1)}h</td>
                        <td className="py-3 px-4 text-right text-gray-500">{openHours.reduce((s, e) => s + (e.travel_hours || 0), 0).toFixed(1)}h</td>
                        <td className="py-3 px-4 text-right text-[#3da5a5]">{openHours.reduce((s, e) => s + (e.hours_worked || 0) + (e.travel_hours || 0), 0).toFixed(1)}h</td>
                        <td className="py-3 px-4 text-right text-gray-500">{openHours.length}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader>
              <CardTitle className="text-gray-800">Arquivar Horas Pagas</CardTitle>
              <p className="text-sm text-gray-500">Selecione funcionário e período. Apenas horas <strong>aprovadas</strong> serão arquivadas.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-800 mb-2 block">Funcionário</Label>
                  <Select value={archiveUser || undefined} onValueChange={setArchiveUser}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800" data-testid="archive-user-select">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-800 mb-2 block">Data Inicial</Label>
                  <Input
                    type="date"
                    value={archiveStart}
                    onChange={(e) => setArchiveStart(e.target.value)}
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    data-testid="archive-start-date"
                  />
                </div>
                <div>
                  <Label className="text-gray-800 mb-2 block">Data Final</Label>
                  <Input
                    type="date"
                    value={archiveEnd}
                    onChange={(e) => setArchiveEnd(e.target.value)}
                    className="bg-gray-50 border-gray-200 text-gray-800"
                    data-testid="archive-end-date"
                  />
                </div>
              </div>

              {/* PREVIEW */}
              {archiveUser && archiveStart && archiveEnd && (
                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4" data-testid="archive-preview">
                  {archivePreviewLoading ? (
                    <p className="text-blue-700 text-sm">Carregando prévia...</p>
                  ) : archivePreview.length === 0 ? (
                    <p className="text-blue-700 text-sm">Nenhuma hora aprovada encontrada para este período.</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-blue-900 font-semibold">
                          Prévia: {archivePreview.length} registro(s) — total {archivePreview.reduce((s,e)=>s+(e.hours_worked||0)+(e.travel_hours||0),0).toFixed(1)}h
                        </p>
                      </div>
                      <div className="max-h-64 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-600 border-b">
                              <th className="text-left py-1 pr-2">Data</th>
                              <th className="text-left py-1 pr-2">Cliente</th>
                              <th className="text-left py-1 pr-2">Endereço</th>
                              <th className="text-right py-1 pr-2">Horas</th>
                              <th className="text-right py-1">Viagem</th>
                            </tr>
                          </thead>
                          <tbody>
                            {archivePreview.map(e => (
                              <tr key={e.id} className="border-b border-blue-100">
                                <td className="py-1 pr-2 text-gray-700">{formatDatePT(e.date)}</td>
                                <td className="py-1 pr-2 text-gray-700">{e.client}</td>
                                <td className="py-1 pr-2 text-gray-500">{e.project || '-'}</td>
                                <td className="py-1 pr-2 text-right text-gray-800 font-medium">{(e.hours_worked||0).toFixed(1)}h</td>
                                <td className="py-1 text-right text-gray-500">{(e.travel_hours||0).toFixed(1)}h</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="flex gap-3">
                <Button 
                  onClick={handleArchive} 
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  disabled={archivePreview.length === 0}
                  data-testid="archive-btn"
                >
                  <Archive className="w-4 h-4 mr-2" />
                  Arquivar {archivePreview.length > 0 ? `(${archivePreview.length})` : ''}
                </Button>
                <Button 
                  onClick={handleUnarchive} 
                  className="flex-1 bg-orange-600 hover:bg-orange-700"
                  disabled={!archiveUser || !archiveStart || !archiveEnd}
                  data-testid="unarchive-btn"
                >
                  <RotateCcw className="w-4 h-4 mr-2" />
                  Desarquivar
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Filtros para horas arquivadas */}
          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader>
              <CardTitle className="text-gray-800">Filtrar Horas Arquivadas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-gray-800">Funcionário</Label>
                  <Select value={archivedFilters.user_id} onValueChange={(v) => setArchivedFilters({...archivedFilters, user_id: v})}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800" data-testid="archived-filter-user"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-800">Cliente</Label>
                  <Select value={archivedFilters.client} onValueChange={(v) => setArchivedFilters({...archivedFilters, client: v})}>
                    <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-800" data-testid="archived-filter-client"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {clients.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-800">Data Inicial</Label>
                  <Input type="date" value={archivedFilters.start_date} onChange={(e) => setArchivedFilters({...archivedFilters, start_date: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-800" />
                </div>
                <div>
                  <Label className="text-gray-800">Data Final</Label>
                  <Input type="date" value={archivedFilters.end_date} onChange={(e) => setArchivedFilters({...archivedFilters, end_date: e.target.value})} className="bg-gray-50 border-gray-200 text-gray-800" />
                </div>
              </div>
              <div className="mt-3">
                <Button variant="outline" size="sm" onClick={() => setArchivedFilters({ user_id: 'all', client: 'all', start_date: '', end_date: '' })} className="border-gray-300 text-gray-800">
                  Limpar filtros
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white shadow-sm border-gray-200">
            <CardHeader>
              <CardTitle className="text-gray-800 flex items-center justify-between">
                <span>Horas Arquivadas ({archivedEntries.length})</span>
                <span className="text-sm font-normal text-gray-500">
                  Total: {archivedEntries.reduce((s, e) => s + (e.hours_worked || 0) + (e.travel_hours || 0), 0).toFixed(1)}h
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {archivedEntries.length === 0 ? (
                <p className="text-gray-400 text-center py-8">Nenhuma hora arquivada encontrada com os filtros aplicados</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="archived-table">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-2 px-3 text-gray-600 font-semibold">Data</th>
                        <th className="text-left py-2 px-3 text-gray-600 font-semibold">Funcionário</th>
                        <th className="text-left py-2 px-3 text-gray-600 font-semibold">Cliente</th>
                        <th className="text-left py-2 px-3 text-gray-600 font-semibold">Endereço</th>
                        <th className="text-right py-2 px-3 text-gray-600 font-semibold">Horas</th>
                        <th className="text-right py-2 px-3 text-gray-600 font-semibold">Viagem</th>
                        <th className="text-left py-2 px-3 text-gray-600 font-semibold">Arquivado em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {archivedEntries.slice(0, 200).map(entry => {
                        const eind = resolveEindklant(entry);
                        return (
                        <tr key={entry.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-2 px-3 text-gray-700 whitespace-nowrap">{formatDatePT(entry.date)}</td>
                          <td className="py-2 px-3 text-gray-700">{entry.user_name}</td>
                          <td className="py-2 px-3 text-gray-700">
                            <span className="font-semibold">{eind.label || entry.client}</span>
                            {eind.bedrijf && eind.bedrijf.toLowerCase() !== (eind.label || entry.client || '').toLowerCase() && (
                              <span className="block text-[10px] text-gray-400">{eind.bedrijf}</span>
                            )}
                          </td>
                          <td className="py-2 px-3 text-gray-500 text-xs">{entry.project || '-'}</td>
                          <td className="py-2 px-3 text-right font-medium text-gray-800">{(entry.hours_worked || 0).toFixed(1)}h</td>
                          <td className="py-2 px-3 text-right text-gray-500">{(entry.travel_hours || 0).toFixed(1)}h</td>
                          <td className="py-2 px-3 text-blue-600 text-xs">{entry.archived_date || '-'}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {archivedEntries.length > 200 && (
                    <p className="text-gray-400 text-center text-sm pt-3">
                      Mostrando 200 de {archivedEntries.length} registros. Use os filtros para refinar.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detailed Report Modal */}
      {showDetailedReport && detailedReportData && (
        <DetailedReport
          open={showDetailedReport}
          onClose={() => setShowDetailedReport(false)}
          userData={detailedReportData.user}
          entries={detailedReportData.entries}
          weekStart={detailedReportData.weekStart}
          weekEnd={detailedReportData.weekEnd}
        />
      )}
    </div>
  );
};

export default ReportsComplete;
